import React, { useEffect, useState } from "react";
import "chartjs-plugin-zoom";
import { Line } from "react-chartjs-2";

import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import "chartjs-adapter-date-fns";
import zoomPlugin from "chartjs-plugin-zoom";
import {Consts} from "./consts";
import {useTranslation} from "react-i18next";
import {getTranslatedLabel} from "../diary/DiaryValuesLineChart";

ChartJS.register(
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin,
);

const colorPalette = [
  "#FF6384",
  "#36A2EB",
  "#FFCE56",
  "#4BC0C0",
  "#9966FF",
  "#FF9F40",
  "#FF6384",
  "#36A2EB",
  "#FFCE56",
  "#4BC0C0",
  "#9966FF",
  "#FF9F40",
  "#FF6384",
  "#36A2EB",
  "#FFCE56",
  "#4BC0C0",
  "#9966FF",
  "#FF9F40",
  "#FF6384",
  "#36A2EB",
];

const transformData = (data) => {
  return data
    ?.map((point) => ({
      x: new Date(point?.x),
      y: typeof point?.y === Consts.BOOLEAN ? (point?.y ? 1 : 0) : point?.y,
      value: point?.value,
      unit: point?.unit,
      endDate: point?.endDate,
    }))?.sort((a, b) => a.x - b.x);
};

const MultiLineChart = ({
  dataSets,
  xLabel,
  yLabel,
  integerYAxis,
  maxYValue,
}) => {
  const [fontSize, setFontSize] = useState(14);
  const { t } = useTranslation();

  useEffect(() => {
    const updateFontSize = () => {
      const rootStyles = getComputedStyle(document.documentElement);

      const mobileXs = parseInt(
        rootStyles?.getPropertyValue("--breakpoint-mobile-xs"),
        10,
      );

      const mobileS = parseInt(
        rootStyles?.getPropertyValue("--breakpoint-mobile-s"),
        10,
      );

      const mobile = parseInt(
        rootStyles?.getPropertyValue("--breakpoint-mobile"),
        10,
      );

      const tablet = parseInt(
        rootStyles?.getPropertyValue("--breakpoint-tablet"),
        10,
      );

      if (window.innerWidth < mobileXs) {
        setFontSize(8);
      } else if (window.innerWidth < mobileS) {
        setFontSize(10);
      } else if (window.innerWidth < mobile) {
        setFontSize(12);
      } else if (window.innerWidth < tablet) {
        setFontSize(14);
      } else {
        setFontSize(16);
      }
    };

    updateFontSize();
    window.addEventListener(Consts.RESIZE, updateFontSize);
    return () => window.removeEventListener(Consts.RESIZE, updateFontSize);
  }, []);

  const chartData = {
    datasets: dataSets?.map((dataSet, index) => ({
      label: getTranslatedLabel(dataSet?.label, t),
      data: transformData(dataSet?.data),
      borderColor: colorPalette[index % colorPalette?.length],
      fill: false,
      borderWidth: dataSet?.connectPoints ? 2 : 0,
      pointBackgroundColor: colorPalette[index % colorPalette?.length],
      highlighted: dataSet?.highlighted,
      pointStyle: dataSet?.highlighted ? Consts.CIRCLE : Consts.RECT,
      pointRadius: dataSet?.highlighted ? 6 : 3,
    })),
  };

  const xValues = dataSets?.flatMap((ds) => ds?.data?.map((point) => new Date(point?.x)));
  // set x axes interval
  const xMin = new Date(Math.min(...xValues));
  xMin?.setHours(0, 0, 0, 0);
  const xMax = new Date(Math.max(...xValues) + 86400000);
  xMax?.setHours(0, 0, 0, 0);

  const calculateEffectiveMaxYValue = () => {
    const yValues = dataSets?.flatMap((dataSet) => transformData(dataSet?.data)?.map((point) => point?.y));
    const maxDataValue = Math.max(...yValues);
    return maxYValue ? maxYValue : Math.ceil(maxDataValue * 1.2);
  };

  const zoomOptions = {
    pan: {
      enabled: true,
      mode: "x",
    },
    zoom: {
      wheel: {
        enabled: true,
      },
      pinch: {
        enabled: true,
      },
      mode: "x",
    },
  };

  const tooltipOptions = {
    callbacks: {
      label: function (context) {
        let label = context?.dataset?.label || "";
        const ind = label?.indexOf("&&");

        if(ind > 0) {
          label = label?.substring(0, ind);
        }

        const pointData = context?.raw;

        const value = pointData?.value
          ? pointData?.value + (pointData?.unit ? " " + pointData?.unit : "")
          : context?.parsed.y;

        const endDateText = pointData?.endDate === "" ? "" : `, ${t("end_date")}: ${pointData?.endDate}`;
        return `${label}: ${value}${endDateText}`;
      },
    },
  };

  const legendOptions = {
    labels: {
      font: {
        size: fontSize,
      },
      usePointStyle: true,
      generateLabels: (chart) => {
        const datasets = chart?.data?.datasets;

        return datasets?.map((dataset, index) => {
          const pointStyle = dataset?.pointStyle || (!dataset?.highlighted ? Consts.CIRCLE : Consts.RECT);

          return {
            text: dataset?.label,
            fillStyle: dataset?.borderColor,
            hidden: !chart?.isDatasetVisible(index),
            datasetIndex: index,
            pointStyle,
            pointRadius: 3,
            boxWidth: 5,
            padding: 5,
          };
        });
      },
    },
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: Consts.TIME,
        min: xMin,
        max: xMax,
        time: {
          unit: Consts.DAY,
          displayFormats: {
            day: "yyyy-MM-dd",
          },
          tooltipFormat: "yyyy-MM-dd",
        },
        title: {
          display: true,
          text: xLabel,
          font: {
            size: fontSize,
          },
        },
        ticks: {
          font: {
            size: fontSize,
          },
        },
      },
      y: {
        beginAtZero: true,
        max: calculateEffectiveMaxYValue(),
        title: {
          display: true,
          text: yLabel,
          font: {
            size: fontSize,
          },
        },
        ticks: {
          stepSize: integerYAxis ? 1 : undefined,
          font: {
            size: fontSize,
          },
          callback: (value) => {
            const numValue = Number(value);
            return Number.isInteger(numValue) ? numValue : numValue.toFixed(0);
          },
        },
      },
    },
    plugins: {
      tooltip: tooltipOptions,
      legend: legendOptions,
      zoom: zoomOptions,
    },
  };

  // comment since use zoom instead of displaying horizontal scrollbar
  /* const pixelPerDatapoint = 40;
  const minWidth = 1000;
  const chartWidth = dataSets?.reduce((maxWidth, dataSet) => {
    const dataPoints = dataSet?.data?.length;
    return Math.max(maxWidth, dataPoints * pixelPerDatapoint);
  }, minWidth);*/

  return (
    <div className="w3-container">
      <div className="chart-scroll-container">
        <div className="chart-container">
          <Line data={chartData} options={options} className="chart-canvas" />
        </div>
      </div>
    </div>
  );
};

export default MultiLineChart;
